// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Loader2, DollarSign, TrendingUp, CreditCard, Download } from 'lucide-react';
import { toast } from 'sonner';

interface GraphicsEarning {
  id: string;
  vendor_id: string;
  amount: number;
  commission_rate: number;
  source_type: string;
  status: 'pending' | 'paid' | 'cancelled';
  earned_at: string;
  paid_at?: string;
  vendor_name?: string;
}

interface GraphicsPayout {
  id: string;
  vendor_id: string;
  amount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  payout_method: string;
  created_at: string;
  processed_at?: string;
  completed_at?: string;
  payout_reference?: string;
  vendor_name?: string;
  venmo_handle?: string;
}

const GraphicsFinanceTab: React.FC = () => {
  const [earnings, setEarnings] = useState<GraphicsEarning[]>([]);
  const [payouts, setPayouts] = useState<GraphicsPayout[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    pendingEarnings: 0,
    paidEarnings: 0,
    totalPayouts: 0,
  });

  useEffect(() => {
    fetchFinanceData();
  }, []);

  const fetchFinanceData = async () => {
    try {
      setIsLoading(true);

      // Fetch earnings with vendor information
      const { data: earningsData, error: earningsError } = await (supabase as any)
        .from('graphics_vendor_earnings')
        .select(`
          *,
          graphics_vendor_profiles!vendor_id(business_name, user_id),
          profiles!graphics_vendor_profiles_user_id_fkey(name)
        `)
        .order('earned_at', { ascending: false });

      if (earningsError) throw earningsError;

      // Fetch payouts with vendor information including venmo handles
      const { data: payoutsData, error: payoutsError } = await (supabase as any)
        .from('graphics_vendor_payouts')
        .select(`
          *,
          graphics_vendor_profiles!vendor_id(business_name, user_id),
          profiles!graphics_vendor_profiles_user_id_fkey(name, venmo_handle)
        `)
        .order('created_at', { ascending: false });

      if (payoutsError) throw payoutsError;

      // Add vendor names to earnings
      const enrichedEarnings = earningsData?.map(earning => ({
        ...earning,
        vendor_name: earning.graphics_vendor_profiles?.business_name || 
                    earning.profiles?.name || 
                    'Unknown Vendor'
      })) || [];

      // Add vendor names and venmo handles to payouts
      const enrichedPayouts = payoutsData?.map(payout => ({
        ...payout,
        vendor_name: payout.graphics_vendor_profiles?.business_name || 
                    payout.profiles?.name || 
                    'Unknown Vendor',
        venmo_handle: payout.profiles?.venmo_handle
      })) || [];

      setEarnings(enrichedEarnings as any);
      setPayouts(enrichedPayouts as any);

      // Calculate stats
      const totalEarnings = enrichedEarnings.reduce((sum, earning) => sum + earning.amount, 0);
      const pendingEarnings = enrichedEarnings.filter(e => e.status === 'pending').reduce((sum, earning) => sum + earning.amount, 0);
      const paidEarnings = enrichedEarnings.filter(e => e.status === 'paid').reduce((sum, earning) => sum + earning.amount, 0);
      const totalPayouts = enrichedPayouts.filter(p => p.status === 'completed').reduce((sum, payout) => sum + payout.amount, 0);

      setStats({
        totalEarnings,
        pendingEarnings,
        paidEarnings,
        totalPayouts,
      });

    } catch (error) {
      console.error('Error fetching finance data:', error);
      toast.error('Failed to load finance data');
    } finally {
      setIsLoading(false);
    }
  };

  const processPayout = async (payoutId: string) => {
    try {
      const { error } = await (supabase as any)
        .from('graphics_vendor_payouts')
        .update({ 
          status: 'completed',
          processed_at: new Date().toISOString(),
          completed_at: new Date().toISOString(),
        })
        .eq('id', payoutId);

      if (error) throw error;

      toast.success('Payout processed successfully');
      fetchFinanceData();
    } catch (error: any) {
      console.error('Error processing payout:', error);
      toast.error(error.message || 'Failed to process payout');
    }
  };

  const formatPrice = (amount: number) => {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getEarningStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'secondary' as const, label: 'Pending' },
      paid: { variant: 'default' as const, label: 'Paid' },
      cancelled: { variant: 'destructive' as const, label: 'Cancelled' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  const getPayoutStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'secondary' as const, label: 'Pending' },
      processing: { variant: 'default' as const, label: 'Processing' },
      completed: { variant: 'default' as const, label: 'Completed' },
      failed: { variant: 'destructive' as const, label: 'Failed' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Badge variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading finance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(stats.totalEarnings)}</div>
            <p className="text-xs text-muted-foreground">All vendor earnings</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Earnings</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{formatPrice(stats.pendingEarnings)}</div>
            <p className="text-xs text-muted-foreground">Awaiting release</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid Earnings</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatPrice(stats.paidEarnings)}</div>
            <p className="text-xs text-muted-foreground">Released to vendors</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Payouts</CardTitle>
            <Download className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{formatPrice(stats.totalPayouts)}</div>
            <p className="text-xs text-muted-foreground">Completed payouts</p>
          </CardContent>
        </Card>
      </div>

      {/* Earnings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Vendor Earnings</CardTitle>
        </CardHeader>
        <CardContent>
          {earnings.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Commission Rate</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Earned Date</TableHead>
                  <TableHead>Paid Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {earnings.map((earning) => (
                  <TableRow key={earning.id}>
                    <TableCell className="font-medium">
                      {earning.vendor_name}
                    </TableCell>
                    <TableCell>{formatPrice(earning.amount)}</TableCell>
                    <TableCell>{earning.commission_rate}%</TableCell>
                    <TableCell className="capitalize">{earning.source_type}</TableCell>
                    <TableCell>
                      {getEarningStatusBadge(earning.status)}
                    </TableCell>
                    <TableCell>{formatDate(earning.earned_at)}</TableCell>
                    <TableCell>
                      {earning.paid_at ? formatDate(earning.paid_at) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Earnings Yet</h3>
              <p className="text-muted-foreground">
                Vendor earnings will appear here when orders are completed.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payouts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Payout Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {payouts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map((payout) => (
                  <TableRow key={payout.id}>
                    <TableCell className="font-medium">
                      {payout.vendor_name}
                    </TableCell>
                    <TableCell>{formatPrice(payout.amount)}</TableCell>
                    <TableCell className="capitalize">
                      <div className="flex items-center gap-1">
                        {payout.payout_method === 'venmo' && <span>💳</span>}
                        {payout.payout_method}
                        {payout.payout_method === 'venmo' && payout.venmo_handle && (
                          <span className="text-xs text-muted-foreground">
                            ({payout.payout_reference || payout.venmo_handle})
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getPayoutStatusBadge(payout.status)}
                    </TableCell>
                    <TableCell>{formatDate(payout.created_at)}</TableCell>
                    <TableCell>
                      {payout.completed_at ? formatDate(payout.completed_at) : '-'}
                    </TableCell>
                    <TableCell>
                      {payout.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => processPayout(payout.id)}
                        >
                          Process
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Download className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Payout Requests</h3>
              <p className="text-muted-foreground">
                Vendor payout requests will appear here.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GraphicsFinanceTab;